import { useEffect, useRef } from 'react';

/**
 * useLazyImage(src)
 *
 * IntersectionObserver-based lazy loader for SVG <image> elements, which do
 * not support the native loading="lazy" attribute.
 *
 * Returns a ref to attach to the SVG <image> element.  The hook sets the
 * element's `href` attribute only when it enters the viewport, producing the
 * same effect as loading="lazy" on a standard <img>.
 *
 * Usage (inside a React component that renders SVG):
 *
 *   const imgRef = useLazyImage(ev.photo);
 *   ...
 *   <image ref={imgRef} width={...} height={...} ... />
 *
 * When the element has no ref-able DOM node (e.g. it hasn't mounted yet) the
 * hook is a no-op.
 *
 * @param {string|null} src  Image URL to load lazily.  If null/undefined the
 *                           hook is a no-op.
 * @returns {React.RefObject}
 */
const useLazyImage = (src) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!src || !ref.current) return;
    const el = ref.current;

    // Already loaded (e.g. on a hot-module-reload re-mount).
    if (el.getAttribute('href') === src) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback for very old browsers: set href immediately.
      el.setAttribute('href', src);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (entries[0].isIntersecting) {
          el.setAttribute('href', src);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' }  // start loading 200px before entering viewport
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  return ref;
};

export default useLazyImage;
